/**
 * Double-Entry Accounting Validation Engine
 * 
 * CRITICAL: This is the SINGLE validation gate for ALL voucher types.
 * Every voucher (CR, BR, CP, BP, JV, SV, PV, DN, CN, etc.) MUST pass
 * through this engine before save. No partial saves allowed.
 *
 * Validates against T_AccountDetails — the master double-entry ledger.
 *
 * Validation order:
 * 1. Check empty rows
 * 2. Validate ledger/account selection (no nulls)
 * 3. Validate debit/credit presence (amount > 0)
 * 4. Prevent both debit AND credit in same row
 * 5. Ensure minimum entries (2 for JV, 1+ for others)
 * 6. Calculate totals
 * 7. Enforce Debit = Credit (with optional tolerance)
 * 8. Validate posting date (within fiscal year)
 * 9. Duplicate check (same date + account + amount)
 * 10. Currency rounding check
 */

// ══════════════════════════════════════════════════════
// TYPES
// ══════════════════════════════════════════════════════

/**
 * @typedef {Object} VoucherLineItem
 * @property {string} accountId - GL Account ID (required, non-empty)
 * @property {string} accountName - Display name
 * @property {number} drAmount - Debit amount (>= 0)
 * @property {number} crAmount - Credit amount (>= 0)
 * @property {string} [billRef] - Bill reference
 * @property {string} [remarks] - Line narration
 */

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} valid - Whether all checks passed
 * @property {string[]} errors - List of specific error messages
 * @property {Object} summary - { totalDr, totalCr, difference, lineCount }
 */

// ══════════════════════════════════════════════════════
// FRONTEND VALIDATION ENGINE
// ══════════════════════════════════════════════════════

const AMOUNT_TOLERANCE = 1.00; // ₹1 rounding tolerance (matches existing DB data)

/**
 * Validate a voucher's double-entry integrity.
 * Called by ALL voucher types before save.
 * 
 * @param {Object} params
 * @param {VoucherLineItem[]} params.lineItems - All line items
 * @param {string} params.voucherType - e.g. 'cash-receipts', 'journal-voucher'
 * @param {string} params.voucherDate - ISO date string
 * @param {string} [params.accountId] - Header account (for Cash/Bank types)
 * @param {boolean} params.isJournal - JV/DN/CN/Contra (Dr/Cr split)
 * @param {boolean} params.isBank - Bank voucher (requires cheque)
 * @param {string} [params.chequeNo] - Cheque number for bank types
 * @param {string} [params.accountLabel] - Label for error messages
 * @param {string} [params.fiscalYearStart] - e.g. '2020-04-01'
 * @param {string} [params.fiscalYearEnd] - e.g. '2025-03-31'
 * @returns {ValidationResult}
 */
export function validateVoucher({
  lineItems = [],
  voucherType = '',
  voucherDate = '',
  accountId = '',
  isJournal = false,
  isBank = false,
  chequeNo = '',
  accountLabel = 'Account',
  hasAccountInHeader = true,
  fiscalYearStart,
  fiscalYearEnd,
  billRefMandatory = false,   // Configurable via Voucher Settings (localStorage per type)
}) {
  const errors = [];
  
  // ── Step 1: Check empty rows ──
  if (lineItems.length === 0) {
    errors.push('No line items added. At least one entry is required.');
  }

  // ── Step 2: Validate ledger selection (no null accounts) ──
  lineItems.forEach((li, i) => {
    if (!li.accountId || li.accountId.trim() === '') {
      errors.push(`Row ${i + 1}: Account Head (Ledger) is not selected. Every row must have a valid account.`);
    }
  });

  // ── Step 3: Validate amounts (> 0) ──
  lineItems.forEach((li, i) => {
    const dr = parseFloat(li.drAmount) || 0;
    const cr = parseFloat(li.crAmount) || 0;
    const amt = parseFloat(li.amount) || 0;
    if (dr <= 0 && cr <= 0 && amt <= 0) {
      errors.push(`Row ${i + 1}: Amount must be greater than 0.`);
    }
  });

  // ── Step 3b: R006 — No negative amounts ──
  lineItems.forEach((li, i) => {
    const dr = parseFloat(li.drAmount) || 0;
    const cr = parseFloat(li.crAmount) || 0;
    const amt = parseFloat(li.amount) || 0;
    if (dr < 0 || cr < 0 || amt < 0) {
      errors.push(`Row ${i + 1}: Negative amounts are not permitted. Use the correct voucher type for reversals.`);
    }
  });

  // ── Step 4: Prevent both Dr AND Cr in same row ──
  lineItems.forEach((li, i) => {
    const dr = parseFloat(li.drAmount) || 0;
    const cr = parseFloat(li.crAmount) || 0;
    if (dr > 0 && cr > 0) {
      errors.push(`Row ${i + 1}: Cannot have both Debit (${dr.toFixed(2)}) and Credit (${cr.toFixed(2)}) in the same row. Split into separate rows.`);
    }
  });

  // ── Step 5: Minimum entries ──
  if (isJournal && lineItems.length < 2 && lineItems.length > 0) {
    errors.push('Journal entry requires minimum 2 line items (at least 1 Debit + 1 Credit).');
  }

  // ── Step 6: Calculate totals ──
  let totalDr = 0, totalCr = 0;
  lineItems.forEach(li => {
    totalDr += parseFloat(li.drAmount) || 0;
    totalCr += parseFloat(li.crAmount) || 0;
  });

  // For non-JV vouchers, the header account creates the balancing entry
  // e.g., Cash Receipt: header Cash A/c Dr = sum of line Cr amounts
  if (!isJournal && lineItems.length > 0) {
    const lineTotal = lineItems.reduce((s, li) => s + (parseFloat(li.crAmount) || parseFloat(li.drAmount) || parseFloat(li.amount) || 0), 0);
    // The header account auto-balances, so Dr should equal Cr after system adds header entry
    // For validation, we just ensure line items have valid amounts
  }

  // ── Step 7: Enforce Debit = Credit (JV types) ──
  if (isJournal && lineItems.length >= 2) {
    const diff = Math.abs(totalDr - totalCr);
    if (diff > AMOUNT_TOLERANCE) {
      const direction = totalDr > totalCr ? 'Credit' : 'Debit';
      const shortfall = diff.toFixed(2);
      errors.push(`Debit total (${totalDr.toFixed(2)}) does not equal Credit total (${totalCr.toFixed(2)}). ${direction} is short by ${shortfall}. Suggest adding ${shortfall} to ${direction} side.`);
    }
  }

  // ── Step 8: Validate posting date ──
  if (!voucherDate) {
    errors.push('Voucher Date is required.');
  } else {
    const dt = new Date(voucherDate);
    if (isNaN(dt.getTime())) {
      errors.push('Voucher Date is invalid.');
    }
    if (fiscalYearStart && fiscalYearEnd) {
      const start = new Date(fiscalYearStart);
      const end = new Date(fiscalYearEnd);
      if (dt < start || dt > end) {
        errors.push(`Voucher Date (${voucherDate}) is outside the fiscal year (${fiscalYearStart} to ${fiscalYearEnd}).`);
      }
    }
  }

  // ── Step 9: Duplicate check removed ──
  // Split entries with the same ledger and amount are valid in accounting (e.g., partial allocations).
  // Previously blocked with an error; now allowed.

  // ── Step 9b: Bill-Wise — Bill Ref mandatory when Against = Bill (R007 spec, if enabled in settings) ──
  if (billRefMandatory) {
    lineItems.forEach((li, i) => {
      if ((li.against === 'Against' || li.againstBill === 'Yes') && !li.billRef) {
        errors.push(`Row ${i + 1}: Bill Reference is required when "Against Bill" is selected (mandatory per Voucher Settings).`);
      }
    });
  }

  // ── Step 10: Account required for non-JV header ──
  if (hasAccountInHeader && !accountId) {
    errors.push(`${accountLabel} is required.`);
  }

  // ── Bank: cheque required ──
  if (isBank && !chequeNo) {
    errors.push('Cheque No is required for bank vouchers.');
  }

  // ── Step 10b: Currency rounding ──
  lineItems.forEach((li, i) => {
    const dr = parseFloat(li.drAmount) || 0;
    const cr = parseFloat(li.crAmount) || 0;
    if (dr > 0 && dr !== parseFloat(dr.toFixed(2))) {
      errors.push(`Row ${i + 1}: Debit amount has more than 2 decimal places. Round to 2 decimals.`);
    }
    if (cr > 0 && cr !== parseFloat(cr.toFixed(2))) {
      errors.push(`Row ${i + 1}: Credit amount has more than 2 decimal places. Round to 2 decimals.`);
    }
  });

  return {
    valid: errors.length === 0,
    errors,
    summary: {
      totalDr: parseFloat(totalDr.toFixed(2)),
      totalCr: parseFloat(totalCr.toFixed(2)),
      difference: parseFloat((totalDr - totalCr).toFixed(2)),
      lineCount: lineItems.length,
      isBalanced: Math.abs(totalDr - totalCr) <= AMOUNT_TOLERANCE,
    },
  };
}

/**
 * Get a human-readable suggestion for balancing a JV
 */
export function getBalanceSuggestion(totalDr, totalCr) {
  const diff = totalDr - totalCr;
  if (Math.abs(diff) <= AMOUNT_TOLERANCE) return null;
  if (diff > 0) return { side: 'Credit', amount: diff.toFixed(2), message: `Add ${diff.toFixed(2)} to Credit side to balance` };
  return { side: 'Debit', amount: Math.abs(diff).toFixed(2), message: `Add ${Math.abs(diff).toFixed(2)} to Debit side to balance` };
}

export default { validateVoucher, getBalanceSuggestion, AMOUNT_TOLERANCE };
